import { CollectionResponse } from '../types/api/CollectionsIndexTypes';
import { ProductResponseWithCollectionId } from '../types/api/ProductsIndexTypes';
import { User } from '../types/api/UsersTypes';
import { SubscriptionDetail } from '../types/api/SubscriptionTypes';
import { ProductOwnershipResponse } from '../types/api/OwnershipTypes';
import { LicenseTypeDetail, LicenseId } from '../types/api/LicenseTypesTypes';
import { BundleView } from '../types/api/BundlesTypes';
import { Dispatch } from 'redux';

import { ENV } from '../constants/environments';

import axios from 'axios';
import licenseHelpers from './licenseHelpers';
import algoliaHelpers from './algolia';
import { addMessage } from '../state/actions/messagesActions';
import { decrementAvailableCredits } from '../state/actions/creditActions';
import { showModal } from '../state/actions/buyMoreCreditsActions';

const purchase = async (
  items: CollectionResponse | ProductResponseWithCollectionId | Array<CollectionResponse | ProductResponseWithCollectionId>,
  user: User,
  queryId: string,
) => {
  const { pushCreditPurchaseEvent } = algoliaHelpers;
  const payload = Array.isArray(items)
    ? {
      product_ids: (items as Array<CollectionResponse | ProductResponseWithCollectionId>).map(item => item.id),
      user,
    }
    : {
      product_ids: [(items as CollectionResponse | ProductResponseWithCollectionId).id],
      user,
    };
  const result = await axios.post('/api/purchase', payload);
  await pushCreditPurchaseEvent(user, Array.isArray(items) ? items : [items], queryId);
  return result;
};

const purchaseBundle = async (
  items: BundleView | Array<BundleView>,
  user: User,
  subscription: SubscriptionDetail,
  dispatch: Dispatch,
): Promise<boolean> => {
  const bundles: Array<BundleView> = Array.isArray(items) ? items : [items];

  const getTotalPrice = (): number => {
    let totalCreditPrice = 0;
    bundles.forEach(b => totalCreditPrice += b.credit_price);
    return totalCreditPrice;
  }

  const checkMissingCredits = (): number => {
    return getTotalPrice() - subscription.current_credit_balance;
  };

  const afterPurchaseHandler = (): void => {
    dispatch(decrementAvailableCredits(getTotalPrice()));
  }

  const missingCredits = checkMissingCredits();
  if (missingCredits > 0) {
    dispatch(showModal('not-enough-credits', missingCredits));
    return false;
  };

  const payload = { bundle_ids: bundles.map(b => b.id) };
  try {
    await axios.post(`${ENV.api.baseURL}/credit-purchases/bundles`, payload, {
      headers: { authorization: user.token }
    });
    afterPurchaseHandler();
    return true;
  } catch (e) {
    dispatch(addMessage({
      body: e?.response?.data?.message || e?.data?.message || e?.message || 'An error has occurred',
      type: 'error',
    }));
    return false;
  }
}

const determineNeedsRepurchase = (
  ownership: ProductOwnershipResponse | undefined,
  collection: CollectionResponse,
): boolean => {
  if (!ownership || collection.free_product) return false;
  return !ownership?.has_legitimate_license;
};

const determinePurchaseLicenseId = (
  ownership: ProductOwnershipResponse,
): LicenseId => {
  return ownership?.purchased_with_license?.id as LicenseId;
}

const determinePurchaseLicenseName = (
  licenseTypes: Array<LicenseTypeDetail>,
  ownership: ProductOwnershipResponse,
): string => {
  const { getLicenseNameById } = licenseHelpers;
  const { determinePurchaseLicenseId } = purchaseHelpers;
  const licenseId = determinePurchaseLicenseId(ownership);
  if (!licenseId) return 'Unspecified';
  return getLicenseNameById(licenseTypes, licenseId);
}

const determinePurchaseLicensedToName = (
  ownership: ProductOwnershipResponse,
): string => {
  return ownership?.purchased_by?.licensee || 'Unknown'
};

const purchaseHelpers = {
  purchase,
  purchaseBundle,
  determineNeedsRepurchase,
  determinePurchaseLicenseName,
  determinePurchaseLicenseId,
  determinePurchaseLicensedToName,
};

export default purchaseHelpers;
